import React, { useState } from 'react';
import { TrendingUp, Send, CheckCircle, AlertCircle, Smartphone } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function MobileRatePushPage() {
  const [gold22k, setGold22k] = useState('');
  const [gold18k, setGold18k] = useState('');
  const [silver999, setSilver999] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [pushing, setPushing] = useState(false);
  const [lastPush, setLastPush] = useState(null);

  const handlePush = async (e) => {
    e.preventDefault();
    
    if (!gold22k || !gold18k || !silver999) {
      toast.error('Please enter all rates');
      return;
    }

    if (!secretKey) {
      toast.error('Please enter the secret key');
      return;
    }

    setPushing(true);
    try {
      const response = await api.post('/kerala-bullion/webhook', {
        gold_22k: parseFloat(gold22k),
        gold_18k: parseFloat(gold18k),
        silver_999: parseFloat(silver999),
        secret_key: secretKey
      });

      if (response.data.success) {
        toast.success('Rates pushed successfully!');
        setLastPush({
          gold_22k: gold22k,
          gold_18k: gold18k,
          silver_999: silver999,
          time: new Date().toLocaleTimeString()
        });
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Invalid secret key');
      } else {
        toast.error('Failed to push rates');
      }
    } finally {
      setPushing(false);
    }
  };

  // Auto-calculate 18K from 22K (approximate)
  const handleGold22kChange = (value) => {
    setGold22k(value);
    if (value && !gold18k) {
      // 18K is approximately 82.5% of 22K
      const estimated18k = Math.round(parseFloat(value) * 0.825);
      setGold18k(estimated18k.toString());
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] to-[#1a3a5c] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#C9A967] rounded-full mb-4">
            <Smartphone className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Quick Rate Push</h1>
          <p className="text-gray-300 mt-2">Push Kerala Bullion rates to Marissa Gold</p>
        </div>

        {/* Form */}
        <form onSubmit={handlePush} className="bg-white rounded-2xl p-6 shadow-xl">
          <div className="space-y-4">
            {/* 22K Gold */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                22K Gold (₹/gram)
              </label>
              <input
                type="number"
                value={gold22k}
                onChange={(e) => handleGold22kChange(e.target.value)}
                placeholder="e.g., 14770"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C9A967] focus:border-transparent"
                inputMode="numeric"
              />
            </div>

            {/* 18K Gold */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                18K Gold (₹/gram)
              </label>
              <input
                type="number"
                value={gold18k}
                onChange={(e) => setGold18k(e.target.value)}
                placeholder="e.g., 12195"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C9A967] focus:border-transparent"
                inputMode="numeric"
              />
            </div>

            {/* 999 Silver */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                999 Silver (₹/gram)
              </label>
              <input
                type="number"
                value={silver999}
                onChange={(e) => setSilver999(e.target.value)}
                placeholder="e.g., 285"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C9A967] focus:border-transparent"
                inputMode="numeric"
              />
            </div>

            {/* Secret Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secret Key
              </label>
              <input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="Enter your secret key"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C9A967] focus:border-transparent"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={pushing}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-[#C9A967] to-[#b8985a] text-white text-lg font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {pushing ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  Pushing...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Push Rates
                </>
              )}
            </button>
          </div>

          {/* Last Push Info */}
          {lastPush && (
            <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
              <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                <CheckCircle className="h-5 w-5" />
                Last Push: {lastPush.time}
              </div>
              <div className="text-sm text-green-600 space-y-1">
                <p>22K: ₹{lastPush.gold_22k}</p>
                <p>18K: ₹{lastPush.gold_18k}</p>
                <p>Silver: ₹{lastPush.silver_999}</p>
              </div>
            </div>
          )}
        </form>

        {/* Tips */}
        <div className="mt-6 text-center text-gray-400 text-sm">
          <p>💡 Tip: When you get a Kerala Bullion notification,</p>
          <p>open this page and enter the rates quickly!</p>
        </div>
      </div>
    </div>
  );
}
